import { ref } from 'vue';
import { Network } from '@capacitor/network';
import { useStore } from 'vue-livestore';
import { tables, events } from '@/livestore/schema.js';
import { useToast } from '@/core/useToast.js';

export const pending = ref(0);
export const drainError = ref<string | null>(null);

let _store: ReturnType<typeof useStore>['store'] | null = null;
function store() {
  if (!_store) throw new Error('useOfflineQueue: call setup() first');
  return _store;
}

export function enqueue(bundle, kind = 'drive_bundle') {
  store().commit(
    events.pendingUploadQueued({
      // bundle_id is a required (non-optional) String in the event schema — not every kind of
      // queued payload has a natural "bundle id" (team-schedule items don't), so default to ''
      // rather than pass undefined through and fail schema validation at commit time.
      bundle_id: bundle.bundleId ?? '',
      created_at: Date.now(),
      id: crypto.randomUUID(),
      kind,
      payload: JSON.stringify(bundle),
    }),
  );
  pending.value = store().query(tables.pendingUploads.select()).length;
}

type SyncFn = (item: { bundleId: string; bundleJson: string }) => Promise<void>;
type SyncFnMap = Record<string, SyncFn>;

// syncFn: a single function that handles every kind, or a { [kind]: syncFn } map.
// When a map is passed, only items whose kind has a matching entry are drained — this lets
// multiple independent callers (Drive sync, team-schedule sync, etc.) each register their own
// listener and safely drain only their own kind of pending item without touching, double-
// processing, or crashing on another caller's items still sitting in the same shared queue.
export async function drain(syncFn: SyncFn | SyncFnMap) {
  const items = store()
    .query(tables.pendingUploads.select())
    .slice()
    .sort((a, b) => a.created_at - b.created_at);
  for (const item of items) {
    if (typeof syncFn === 'function') {
      await syncFn({ bundleId: item.bundle_id, bundleJson: item.payload });
    } else {
      const fn = syncFn[item.kind];
      if (!fn) continue; // not this caller's kind — leave it for whoever owns it
      await fn({ bundleId: item.bundle_id, bundleJson: item.payload });
    }
    store().commit(events.pendingUploadCleared({ id: item.id }));
  }
  pending.value = store().query(tables.pendingUploads.select()).length;
}

export function useOfflineQueue(syncFn: SyncFn | SyncFnMap) {
  const { store: s } = useStore();
  _store = s;

  function setup() {
    pending.value = s.query(tables.pendingUploads.select()).length;
    const { show: toast } = useToast();
    Network.addListener('networkStatusChange', ({ connected }) => {
      if (connected)
        drain(syncFn).catch(e => {
          const msg = e instanceof Error ? e.message : String(e);
          drainError.value = msg;
          toast(`Sync drain failed: ${msg}`, { variant: 'error' });
        });
    });
  }

  return { drain: () => drain(syncFn), enqueue, pending, setup };
}
